import { Array, identity, pipe } from 'effect'

import { CORE_CONSTANT } from '../../../../constant.ts'
import type {
  ViewQueryAggregation,
  ViewQueryList,
  ViewQueryNode,
  ViewQueryPivot,
} from '../../../../schemas/query/index.ts'
import type { ViewUiKpi, ViewUiKpiPivotColumn } from '../../../../schemas/ui/kpi.ts'
import type {
  KpiDateAlias,
  ViewConfigCustomFilter,
  ViewConfigDimensionFilter,
  ViewConfigField,
  ViewConfigFilterNode,
  ViewConfigKpiMeasure,
  ViewConfigMeasure,
  ViewConfigPivot,
  ViewConfigPropertyFilter,
  ViewConfigSheet,
  ViewConfigTreeNode,
} from '../../../../schemas/view_config/index.ts'
import { defaultCursor } from '../../../query/executeViewQuery.ts'
import { generateSql, unrestrictedNodePredicateResolver } from '../../../query/executeViewQuery/generateSql.ts'
import { flatNodes } from '../../../query/executeViewQuery/util.ts'
import { getNode, getNodeByName } from '../../../util/node.ts'
import type { CompileContext } from '../../common.ts'
import { generateSelectOptionsWithColor } from './compileSheetViewConfigFields.ts'
import { compileSheetViewConfigFilterTree } from './compileSheetViewConfigFilterTree.ts'
import { compileSheetViewConfigTree } from './compileSheetViewConfigTree.ts'
import type { DimensionWithProperty } from './dimensionWithProperty.ts'
import { expandChildrenByReferenceField } from './expandChildrenByReferenceField.ts'
import { compileMeasure } from './measure/compileMeasure.ts'
import { generateDimensionField } from './measure/generateDimensionField.ts'
import { pruneSheetConfigTree } from './pruneSheetConfigTree.ts'
import { findConfigField, searchFieldAndPropertyForDimension } from './searchFieldAndPropertyForDimension.ts'

interface CompileSheetAndMeasureResult {
  query: ViewQueryAggregation
  ui: Pick<ViewUiKpi, 'pivot'>
}

//
// sheetに対してmeasureとpivotを渡して、集計用のqueryとuiを得る
//
// eslint-disable-next-line complexity,@typescript-eslint/max-params
export function compileSheetAndMeasure(
  measure: ViewConfigMeasure | ViewConfigKpiMeasure,
  config: ViewConfigSheet,
  configPivot: ViewConfigPivot | undefined,
  kpiDateAlias: KpiDateAlias | undefined,
  context: CompileContext,
): CompileSheetAndMeasureResult | undefined {
  const measureNodeNames = extractMeasureNodeNames(measure, config)
  const rowDimensionFields = (configPivot?.rows ?? []).map((d) =>
    searchFieldAndPropertyForDimension(d, config, kpiDateAlias, context),
  )
  const columnDimensionFields = (configPivot?.columns ?? []).map((d) =>
    searchFieldAndPropertyForDimension(d, config, kpiDateAlias, context),
  )
  const dimensionsWithoutRollupDimensionFields = (configPivot?.dimensionsWithoutRollup ?? []).map((d) =>
    searchFieldAndPropertyForDimension(d, config, kpiDateAlias, context),
  )
  const filterTreeWithoutFieldTypeLeaf = convertFieldLeaf(config.filterTree, config.fields ?? [], context)

  const uiPivot = {
    rows: rowDimensionFields.map((x): ViewUiKpiPivotColumn => generatePivotColumnUi(x, 'row')),
    columns: columnDimensionFields.map((x): ViewUiKpiPivotColumn => generatePivotColumnUi(x, 'column')),
    // ...dimensionsWithoutRollupDimensionFields.map((x): ViewUiKpiPivotColumn => generatePivotColumnUi(x, 'column')), // いったん、目標のみを想定すると不要なはず
  }
  const ui = {
    pivot: uiPivot,
  }

  //
  // 序盤はsheetとほぼ同じ
  //
  // pruneを行うために、fieldsを求める。fieldsはmeasuresとdimensionsで使うものだけとする。
  const fields = Array.flatMapNullishOr(
    [
      ...rowDimensionFields.map((x) => x.field),
      ...columnDimensionFields.map((x) => x.field),
      ...dimensionsWithoutRollupDimensionFields.map((x) => x.field),
    ],
    identity,
  )
  const prunedTree = pruneSheetConfigTree({
    tree: config.tree,
    fields,
    filterTree: {
      logicalOperator: `and`,
      leafs: [],
      children: Array.flatMapNullishOr(
        [
          filterTreeWithoutFieldTypeLeaf,
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: 2023/01/14 集計側の絞り込みを考慮するためには、このようにtreeを書き換えてから計算する必要がある。
          //       この対応のためには他の箇所も修正が必要で、現状の開発ステータスでやるべきではないと判断し、一旦後回し。（現状は絞り込みは集計項目に対してしかかけられないので問題ない）
          // ...measuresWithField.flatMap(({ measure }) =>
          //   measure.type === 'preset' ? measure.filterTree : undefined,
          // ),
        ],
        identity,
      ),
    },
    nodeNames: measureNodeNames,
    dimensionNodeSearcher: (d) => {
      const dimensionWithField = searchFieldAndPropertyForDimension(d, config, kpiDateAlias, context)
      return dimensionWithField.field?.property.nodeName
    },
  })

  const expandedTree = expandChildrenByReferenceField(
    prunedTree,
    {
      fields,
      filterTree: filterTreeWithoutFieldTypeLeaf,
    },
    context,
  )
  const tree = compileSheetViewConfigTree(expandedTree, context)
  if (tree === undefined || expandedTree === undefined) {
    if (config.tree === undefined) {
      context.logs.error(`${context.resources.view.name}: 初期設定が終わっていません`)
    } else {
      context.logs.error(`${context.resources.view.name}: 指定したオブジェクトが見つかりません。${config.tree.name}`)
    }
    return undefined
  }
  const queryNodes = flatNodes(tree)
  const configNodes = flatNodes(expandedTree)

  //
  // fields,filter,dimensions,measuresのコンパイル
  //
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 事前にfilterTreeのfield typeを、propertyに変更
  const filterTree = compileSheetViewConfigFilterTree(
    filterTreeWithoutFieldTypeLeaf,
    queryNodes,
    configNodes,
    [],
    context,
    kpiDateAlias,
    config,
  )
  const pivot: ViewQueryPivot | undefined =
    configPivot === undefined
      ? undefined
      : {
          rows: Array.flatMapNullishOr(
            rowDimensionFields.map((x, index) =>
              generateDimensionField(x, { queryNodes, configNodes }, context, true, rowDimensionFields.slice(0, index)),
            ),
            identity,
          ),
          columns: Array.flatMapNullishOr(
            columnDimensionFields.map((x, index) =>
              generateDimensionField(
                x,
                { queryNodes, configNodes },
                context,
                true,
                columnDimensionFields.slice(0, index),
              ),
            ),
            identity,
          ),
          dimensionsWithoutRollup: Array.flatMapNullishOr(
            dimensionsWithoutRollupDimensionFields.map((x) =>
              generateDimensionField(x, { queryNodes, configNodes }, context, false, []),
            ),
            identity,
          ),
        }
  const compiledMeasure = compileMeasure(
    measure,
    config,
    {
      queryNodes,
      configNodes,
      fields: [],
    },
    context,
  )
  // 多重度を考慮しないコンパイル結果
  const result: CompileSheetAndMeasureResult = {
    query: {
      type: 'aggregation',
      tree,
      measures: Array.flatMapNullishOr([compiledMeasure], identity),
      filterTree,
      pivot,
      sorters: [],
    },
    ui,
  }

  //
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 複数のノードにまたがるとき（＝カスタムSQL）のケースでの多重度の考慮は一旦諦める
  // このケースでは、普通にコンパイル結果を返すだけ
  //
  const measureNodePaths = compiledMeasure?.nodePaths ?? []
  const [measureNodePath] = measureNodePaths
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (measureNodePath === undefined || measureNodePaths === undefined || measureNodePaths.length > 1) {
    return result
  }

  //
  // 多重度の考慮が必要ない場合は、普通にコンパイル結果を返す
  //
  const measureNode = getNode(tree, measureNodePath)
  if (measureNode === undefined) {
    // ありえないはず
    context.logs.error(`ノードが見つかりません。 ${measureNodePath.join('.')}`)
    return undefined
  }
  const validMultiplicityNode = pruneInvalidMultiplicityNode(tree, measureNode) ?? tree
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (validMultiplicityNode === undefined) {
    throw new Error(`invalid validMultiplicityNode`) // ありえないはず
  }
  const isValidMultiplicity = flatNodes(validMultiplicityNode).length === flatNodes(tree).length
  if (isValidMultiplicity || context.additionalConfig?.skipMultiplicityCheck === true) {
    return result
  }

  //
  // 重複が発生してしまうケースでも、重複を避けられないケースはそのまま返す
  // 現状、以下のみが該当
  // ・分析軸に1:NのN側の軸を指定したとき
  // eslint-disable-next-line sonarjs/todo-tag
  // ・カスタムSQLで1:NのN側の軸を指定したとき（TODO: これは丁寧に対処すれば、この後の処理と同じ話になるので対応可能）
  //
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  const validNodes = validMultiplicityNode === undefined ? [] : flatNodes(validMultiplicityNode)
  const validNodeNames = new Set(validNodes.map((x) => x.name))
  const ds = [...rowDimensionFields, ...columnDimensionFields, ...dimensionsWithoutRollupDimensionFields]
  const hasInvalidMultiplicityDimension = ds.some(
    (d) => d.field !== undefined && !validNodeNames.has(d.field.property.nodeName),
  )
  if (hasInvalidMultiplicityDimension) {
    return result
  }

  //
  // 多重度が異なるとき、そのまま集計すると重複が発生してしまうため、
  // 「サブクエリでdistinctしたidをjoinする」という手段を使う。すなわち、以下の操作を行う
  // ・treeの変更。サブクエリを実行した結果をINNER JOINする
  // ・フィルタはサブクエリ側で実行するので不要
  // ・dimensionsは、多重度が再発しないようなtreeの組み立てをおこなった上で、残ったノードに対してのみのdimensionを残す
  //
  const listQuery: ViewQueryList = {
    type: 'list',
    tree,
    fields: [],
    filterTree,
    sorters: [],
  }
  return {
    query: {
      type: 'aggregation',
      tree: {
        ...validMultiplicityNode,
        children: [
          ...(validMultiplicityNode.children ?? []),
          generateSubQuery(listQuery, measureNode, context.additionalConfig?.kpiParameter?.queryParameter),
        ],
      },
      measures: Array.flatMapNullishOr([compiledMeasure], identity),
      filterTree: {
        // サブクエリでフィルタ済みなので、フィルタする必要はない
        logicalOperator: `and`,
        leafs: [],
        children: [],
      },
      pivot,
      // eslint-disable-next-line sonarjs/todo-tag
      sorters: [], // TODO
    },
    // eslint-disable-next-line sonarjs/todo-tag
    ui, // TODO: 残ったdimensionsのみにすべきか？
  }
}

function extractMeasureNodeNames(measure: ViewConfigMeasure | ViewConfigKpiMeasure, sheet: ViewConfigSheet) {
  switch (measure.type) {
    case 'custom': {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return measure.dependentNodeNames ?? []
    } // 2023/02/27 invalidなconfigがいくつかあるらしく、dependentNodeNamesがundefinedになっているので一旦応急処置
    case 'kpiCustom': {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return measure.dependentNodeNames ?? []
    }
    case 'kpiPreset': {
      return [measure.property.nodeName]
    }
    case 'preset': {
      const field = (sheet.fields ?? []).find((field) => field.measures?.some((x) => x.name === measure.name) ?? false)
      return Array.flatMapNullishOr([field?.property.nodeName], identity)
    }
  }
}

//
// typeがfieldのleafを、propertyに変換する。
// 本来はこの処理を行わず、そのままfilterのロジックで処理したかったが、多重度の計算のためにフィルタがどのノードに紐づくかを考慮する必要があり、
// この辺を適切にやるには結局フィールドからプロパティに変換したほうが楽だったので、通常のpropertyのfilterに変換する処理を行う
//
export function convertFieldLeaf(
  filterNode: ViewConfigFilterNode | undefined,
  fields: ViewConfigField[],
  context: CompileContext,
): ViewConfigFilterNode | undefined {
  if (filterNode === undefined) {
    return undefined
  }

  return {
    ...filterNode,
    leafs: Array.flatMapNullishOr(
      filterNode.leafs,
      (leaf): ViewConfigPropertyFilter | ViewConfigDimensionFilter | ViewConfigCustomFilter | undefined => {
        if (leaf.type !== 'field') {
          return leaf
        }

        const field = findConfigField(leaf.fieldName, fields)
        if (field === undefined) {
          context.logs.error(
            `絞り込み用のフィールドが見つかりません。fieldName: ${leaf.fieldName} fields: ${fields
              .map((x) => x.property.propertyName)
              .join(',')}`,
          )
          return undefined
        }
        return {
          ...leaf,
          // eslint-disable-next-line sonarjs/todo-tag
          filterDateTimeType: 'datetime', // TODO
          type: 'property',
          property: field.property,
        }
      },
    ),
    children: Array.flatMapNullishOr(filterNode.children, (childNode) => convertFieldLeaf(childNode, fields, context)),
  }
}

// 「多重度」が正しくなるように、不適切なノードを削除する。
// 「正しい多重度になっている」とは、対象のノードについて以下を満たすことである
// ・子孫ノード、または親戚ノードに、1:Nの関係となるノードが存在しない
// すなわち、nodePathに含まれるノード以外に、1:Nの関係となっているノードが存在しないことを確認すれば良い
export function pruneInvalidMultiplicityNode(
  node: ViewQueryNode,
  targetNode: ViewQueryNode,
): ViewQueryNode | undefined {
  // 多重度を増やしてしまうノードであれば削除
  if (node.read.join?.relation === 'one_to_many' && !targetNode.path.includes(node.name)) {
    return undefined
  }

  return {
    ...node,
    children: pipe(
      node.children ?? [],
      Array.flatMapNullishOr((childNode) => pruneInvalidMultiplicityNode(childNode, targetNode)),
    ),
  }
}

// dimensionのために、configNodeのレイヤーで、多重度がおかしいものを弾く
// 判定は集計項目があるノード以下でやればOK
function pruneInvalidMultiplicityConfigNodeForTargetNode(
  tree: ViewConfigTreeNode | undefined,
  targetNodeName: string,
): ViewConfigTreeNode | undefined {
  if (tree === undefined) {
    return undefined
  }

  // pathの取得
  const targetNode = getNodeByName(tree, targetNodeName)
  if (targetNode === undefined) {
    return undefined
  }
  // このノード以下で、one_to_manyなものを弾く
  return pruneInvalidMultiplicityConfigNode(tree, targetNode.path)
}

function pruneInvalidMultiplicityConfigNode(
  node: ViewConfigTreeNode,
  measureNodePath: string[],
): ViewConfigTreeNode | undefined {
  // one_to_manyの判定を、一旦nodeNameのみで判定する（ほぼこれで間違いないはず。one_to_oneが存在すると微妙になる）
  // nodeNameが一致すれば、すなわちone_to_manyであり、多重度が不適切になるため、ここでは除外する
  // 多重度判定のロジックはpruneInvalidMultiplicityNodeのコメントを参照
  if (node.referenceToProperty?.nodeName === node.name && !measureNodePath.includes(node.name)) {
    return undefined
  }
  return {
    ...node,
    children: pipe(
      node.children ?? [],
      Array.flatMapNullishOr((childNode) => pruneInvalidMultiplicityConfigNode(childNode, measureNodePath)),
    ),
  }
}

function generateSubQuery(
  viewQuery: ViewQueryList,
  node: ViewQueryNode,
  queryParameter: Record<string, string | undefined> | undefined,
): ViewQueryNode {
  // queryParameterはpresetsのためだけにもっている値であり、本来はqueryの解決のためだけに使われるが、
  // ここでgenerateSql()する都合で、このタイミングでmustache parameterを解決する必要があり、仕方なく以下の実装をしている
  const parameter =
    queryParameter === undefined
      ? {}
      : // eslint-disable-next-line sonarjs/no-nested-conditional
        queryParameter.periodName !== undefined && queryParameter.periodName !== '全期間'
        ? {
            startAt: ``,
            endAt: ``,
          }
        : queryParameter
  const subQueryNodeName = `sub`
  const sql = generateSql(
    {
      ...viewQuery,
      fields: [
        {
          name: `id`,
          nodePath: node.path,
          read: {
            sql: `"${node.name}".id`,
            distinct: true,
          },
          meta: {
            label: ``,
            fieldType: `string`,
            dependedPropertyNamesWithStreamName: [],
          },
        },
      ],
      extra: {
        asSubQuery: true,
      },
    },
    defaultCursor,
    parameter,
    { resolveNodePredicate: unrestrictedNodePredicateResolver },
  )
  return {
    name: subQueryNodeName,
    path: [node.name, subQueryNodeName],
    read: {
      // eslint-disable-next-line sonarjs/todo-tag
      tableSql: sql, // TODO
      tableSqlTransparency: 'opaque',
      tableType: `subquery`,
      idColumn: `id`,
      join: {
        relation: `many_to_one`,
        joinType: `INNER JOIN`,
        joinOn: {
          type: `id`,
          parentNodeName: node.name,
          parentNodeColumnName: `id`,
          currentNodeName: subQueryNodeName,
          currentNodeColumnName: `id`,
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: 不毛なので削除したい
          meta: {
            parentNodeTableSql: ``,
            parentNodeLabel: ``,
            parentNodeColumnLabel: ``,
            parentNodeStreamName: ``,
            parentNodePropertyName: ``,
            currentNodeTableSql: ``,
            currentNodeLabel: ``,
            currentNodeColumnLabel: ``,
            currentNodeStreamName: ``,
            currentNodePropertyName: ``,
          },
        },
        search: {
          sql: ``,
          dependedPropertyNames: [],
        },
      },
    },
    meta: {
      label: ``,
      dependedStreamNames: [],
    },
  }
}

function generatePivotColumnUi(x: DimensionWithProperty, type: 'row' | 'column'): ViewUiKpiPivotColumn {
  const { dimension } = x
  return {
    // eslint-disable-next-line sonarjs/todo-tag
    key: Array.flatMapNullishOr([dimension.dateSpan, dimension.key], identity).join('_'), // TODO
    label: dimension.label,
    fieldType: getPivotColunnFieldType(x, type),
    fieldMetaType: x.property?.meta ?? undefined,
    selectOptions: generateSelectOptionsWithColor(x.property?.selectOptions ?? undefined),
  }
}

function getPivotColunnFieldType({ dimension, property }: DimensionWithProperty, type: 'row' | 'column') {
  if (type === 'column') {
    return 'kpi' as const
  }
  if (dimension.key === CORE_CONSTANT.KPI_PIVOT_USER_DIMENSION().key) {
    return 'user' as const
  }
  if (property?.referenceTo !== undefined) {
    return 'reference' as const
  }
  if (property?.type === 'date' || property?.type === 'datetime') {
    return 'date' as const
  }
  return 'string' as const
}
